import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, Clock } from "lucide-react";
import { thumb } from "@/lib/img";
import useSEO from "@/hooks/useSEO";
import { BLOG_POSTS } from "@/data/blogData";
import { useBlogPost } from "@/hooks/useBlog";
import { getExpoBySlug } from "@/data/clientsData";
import { organizationSchema, breadcrumbSchema, faqSchema, SITE_URL, LOGO } from "@/lib/seo-schemas";
import QuickQuote from "@/components/QuickQuote";

const absolute = (src) => (src.startsWith("http") ? src : `${SITE_URL}${src}`);

const BlogPostPage = () => {
  const { slug } = useParams();
  const { post, loading } = useBlogPost(slug);

  useSEO({
    title: post ? `${post.title} | Stagehook Global` : "Blog | Stagehook Global",
    description: post?.description,
    keywords: post?.keywords,
    image: post ? absolute(post.heroImage) : undefined,
    path: `/blog/${slug}`,
    jsonLd: post
      ? [
          organizationSchema(),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${post.slug}` }]),
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.description,
            image: absolute(post.heroImage),
            datePublished: post.date,
            dateModified: post.date,
            author: { "@type": "Organization", name: "Stagehook Global", url: SITE_URL },
            publisher: { "@type": "Organization", name: "Stagehook Global", logo: { "@type": "ImageObject", url: LOGO } },
            mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
          },
          faqSchema(post.faqs.map((f) => ({ question: f.q, answer: f.a }))),
        ]
      : undefined,
  });

  if (loading) return <main className="pt-[100px] pb-24"><div className="shg-container text-sm text-[var(--text-muted)]">Loading article…</div></main>;
  if (!post) return <Navigate to="/404" replace />;

  const related = (post.relatedExpos || []).map(getExpoBySlug).filter(Boolean);
  const more = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <main data-testid="blog-post-page" className="pt-[100px] pb-24">
      <div className="shg-container">
        <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm text-[var(--text-soft)] hover:text-[var(--red)] mb-8 transition-colors"><ArrowLeft size={16} /> All articles</Link>

        <div className="grid lg:grid-cols-12 gap-12">
          <article className="lg:col-span-8">
            <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.2em] uppercase mb-5">
              <span style={{ color: "var(--red)" }}>{post.category}</span><span className="text-[var(--text-muted)]">·</span>
              <span className="text-[var(--text-muted)] flex items-center gap-1"><Clock size={11} />{post.readTime} min read</span>
              <span className="text-[var(--text-muted)]">·</span><time className="text-[var(--text-muted)]" dateTime={post.date}>{post.date}</time>
            </div>
            <h1 data-testid="blog-title" className="font-display text-4xl md:text-6xl leading-[0.95] mb-6">{post.title}</h1>
            <p className="text-lg text-[var(--text-soft)] leading-relaxed mb-8">{post.intro}</p>
            <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-12"><img src={post.heroImage} alt={post.title} className="w-full h-full object-cover" /></div>

            {post.sections.map((s) => (
              <section key={s.h2} className="mb-10">
                <h2 className="font-display text-3xl md:text-4xl mb-4">{s.h2}</h2>
                {s.paragraphs?.map((p, i) => <p key={i} className="text-[var(--text-soft)] text-[16px] leading-relaxed mb-4">{p}</p>)}
                {s.bullets && (
                  <ul className="mt-2">{s.bullets.map((b, i) => (
                    <li key={i} className="flex gap-3 items-start mb-3"><Check size={18} className="text-[var(--red)] mt-1 flex-shrink-0" /><span className="text-[var(--text-soft)]">{b}</span></li>
                  ))}</ul>
                )}
              </section>
            ))}

            <section data-testid="blog-faq" className="mt-14 pt-10 border-t" style={{ borderColor: "var(--line)" }}>
              <h2 className="font-display text-3xl mb-6">Frequently asked questions</h2>
              {post.faqs.map((f) => (
                <details key={f.q} className="mb-3 rounded-xl p-5" style={{ background: "var(--surface)", border: "1px solid var(--line)" }}>
                  <summary className="cursor-pointer font-medium">{f.q}</summary>
                  <p className="text-[var(--text-soft)] text-sm leading-relaxed mt-3">{f.a}</p>
                </details>
              ))}
            </section>

            {related.length > 0 && (
              <section className="mt-14">
                <h2 className="font-display text-2xl mb-5">Related expo projects</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {related.map((e) => (
                    <Link key={e.slug} to={`/expo/${e.slug}`} data-testid={`related-expo-${e.slug}`} className="shg-card p-4 flex items-center gap-4 group">
                      <img src={thumb(e.heroImage)} alt={`${e.name} stand in ${e.city}`} loading="lazy" decoding="async" className="w-20 h-14 object-cover rounded-md" />
                      <div><div className="font-display text-base group-hover:text-[var(--red)] transition-colors">{e.name}</div><div className="text-xs text-[var(--text-muted)]">{e.city}, {e.countryName}</div></div>
                      <ArrowUpRight size={14} className="ml-auto text-[var(--red)]" />
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-6">
              <QuickQuote source="blog-post" />
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] text-[var(--text-muted)] uppercase mb-3">More guides</div>
                {more.map((p) => (
                  <Link key={p.slug} to={`/blog/${p.slug}`} className="block py-3 border-b text-sm hover:text-[var(--red)] transition-colors" style={{ borderColor: "var(--line)" }}>{p.title}</Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default BlogPostPage;
